import { Link, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import PictureImage from '../Common/PictureImage';
import Canonical from '../../seo/Canonical';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import NotFound from '../NotFound/NotFound';
import './FulfilmentFacilityDetail.css';

type FacilitySlug =
  | 'wms-control-centre'
  | 'quality-assurance'
  | 'pick-pack-station'
  | 'amazon-fba-prep'
  | 'polybagging-protection';

type FacilityCard = {
  slug: FacilitySlug;
  title: string;
  short: string;
  image: string;
  overlayTitle: string;
  bullets: ReactNode[];
  /** Extra prose under the hero intro (optional). */
  introExtra?: ReactNode;
  /** Quote / contact panel copy (optional — defaults to CTA buttons only). */
  quotePanel?: ReactNode;
  /** Consultation blurb above secondary CTAs (optional). */
  consultBlurb?: ReactNode;
  /** Tour section body (optional). */
  tourBlurb?: ReactNode;
};

const facilityCards: FacilityCard[] = [
  {
    slug: 'wms-control-centre',
    title: 'WMS Control Centre',
    overlayTitle: 'WMS Control Centre',
    short: 'Live inventory visibility with real-time task orchestration.',
    image: '/Images/WMS.webp',
    bullets: [
      'Real-time pick/pack queueing and task assignment',
      'Scanner-verified workflow stages',
      'Operational control with visibility across channels',
    ],
  },
  {
    slug: 'quality-assurance',
    title: 'Quality Assurance',
    overlayTitle: 'Quality Assurance',
    short: 'Inspection & QC checkpoints to reduce damage, returns and chargebacks.',
    image: '/Images/Inventory_inspection.webp',
    bullets: [
      'Inbound inspection before items enter fulfilment',
      'Barcode scanning and QC checks',
      'Consistent pack-ready standards',
    ],
  },
  {
    slug: 'pick-pack-station',
    title: 'Pick & Pack Station',
    overlayTitle: 'Pick & Pack Station',
    short:
      'UK pick and pack station for ecommerce orders — WMS-routed picks, barcode accuracy checks and same-day dispatch capacity.',
    image: '/Images/OPTIMIZED-CASE-PACKAGING.webp',
    introExtra: (
      <>
        This is where Pick Pack Pro runs day-to-day{' '}
        <Link to="/blog/pick-and-pack-fulfilment-uk-explained">pick and pack fulfilment</Link> for Amazon FBM, Shopify,
        TikTok Shop and wholesale orders. If you searched for a UK pick and pack station or fulfilment pick face, this
        zone is the operational heart of our{' '}
        <Link to="/ecommerce-fulfillment-services">ecommerce fulfilment services</Link> — scan-led picks, packing to
        your rules, and carrier labels before collection.
      </>
    ),
    bullets: [
      <>
        Wave and zone planning for efficient{' '}
        <Link to="/blog/same-day-dispatch-fulfilment-that-scales">same-day dispatch</Link> cut-offs
      </>,
      'Optimised packing for speed and protection — branded inserts available',
      '99.9% accuracy target with barcode verification at pick and pack',
      <>
        One station feeding <Link to="/multi-channel-fulfillment-services">multi-channel fulfilment</Link> and{' '}
        <Link to="/seller-fulfilled-prime-services">Seller Fulfilled Prime</Link> workflows
      </>,
    ],
    quotePanel: (
      <>
        <h2>Quote: UK pick and pack from this station</h2>
        <p>
          Tell us your daily order volume and channels. We price pick fees, packing materials and labels transparently —
          see <Link to="/3pl-pricing-explained">3PL pricing UK</Link> and{' '}
          <Link to="/pricing-fulfilment-services">fulfilment pricing</Link>, then{' '}
          <Link to="/contact-fulfillment-services">request a custom quote</Link>.
        </p>
      </>
    ),
  },
  {
    slug: 'amazon-fba-prep',
    title: 'Amazon FBA Prep Area',
    overlayTitle: 'Amazon FBA Prep Area',
    short:
      'Accurate FNSKU labelling and professional preparation ensuring every shipment meets Amazon fulfilment requirements.',
    image: '/Images/FNSKU_LABELING.webp',
    introExtra: (
      <>
        This zone is where Pick Pack Pro runs compliant{' '}
        <Link to="/amazon-fba-prep-services">Amazon FBA prep services</Link> for UK sellers — from private label and
        wholesale inbounds to channel mixes that also need{' '}
        <Link to="/multi-channel-fulfillment-services">multi-channel fulfilment</Link> or{' '}
        <Link to="/seller-fulfilled-prime-services">Seller Fulfilled Prime</Link>.
      </>
    ),
    bullets: [
      <>
        Accurate <Link to="/blog/fnsku-labelling-service-uk">FNSKU labelling</Link> with Amazon-compliant preparation
        for every shipment
      </>,
      'Careful handling to keep inventory protected, organised, and fulfilment-ready',
      'Clear documentation and efficient workflows for faster inventory processing',
    ],
    quotePanel: (
      <>
        <h2>Contact Pick Pack Pro: UK Fulfilment Quote</h2>
        <p>
          Get in touch with Pick Pack Pro for a tailored UK fulfilment quote. Whether you need{' '}
          <Link to="/amazon-fba-prep-centre-services">Amazon FBA prep centre</Link> support,{' '}
          <Link to="/blog/pick-and-pack-fulfilment-uk-explained">pick and pack services</Link>, secure warehousing at
          our <Link to="/our-fulfilment-center">UK fulfilment centre</Link>, or complete{' '}
          <Link to="/logistics-services">3PL logistics</Link>, our experienced team is here to help. Complete the form
          on our contact page, and we&apos;ll provide a customised fulfilment solution designed around your business,
          typically within 24 hours. Transparent fee structure tips are also covered in our{' '}
          <Link to="/3pl-pricing-explained">3PL pricing UK</Link> guide and{' '}
          <Link to="/amazon-fba-prep-pricing">FBA prep pricing</Link> page.
        </p>
      </>
    ),
    consultBlurb: (
      <>
        <h2>Book a Free Consultation Call</h2>
        <p>
          Speak with our Amazon FBA prep experts for personalised guidance on the right fulfilment solution for your
          business. Whether you&apos;re launching a new product, scaling your Amazon store, or looking for reliable{' '}
          <Link to="/amazon-fba-prep-services">Amazon FBA prep services in the UK</Link>, we&apos;ll provide practical
          advice with no pressure or obligation. New to FBA? Explore our guide on{' '}
          <Link to="/blog/what-is-fba-prep">What Is FBA Prep?</Link> before your consultation — or compare prep models
          for <Link to="/amazon-fba-prep-private-label">private label</Link> and{' '}
          <Link to="/amazon-fba-prep-wholesale">wholesale</Link> sellers.
        </p>
      </>
    ),
    tourBlurb: (
      <>
        Book a warehouse tour and discover how our{' '}
        <Link to="/fulfilment-facility/wms-control-centre">WMS-powered fulfilment operations</Link> streamline
        inventory, <Link to="/fulfilment-facility/pick-pack-station">pick and pack</Link>, Amazon FBA prep, and
        order dispatch from goods in to delivery — including{' '}
        <Link to="/amazon-fba-shipments">Amazon FBA shipments</Link> and protective{' '}
        <Link to="/fulfilment-facility/polybagging-protection">polybagging</Link> when products need it.
      </>
    ),
  },
  {
    slug: 'polybagging-protection',
    title: 'Polybagging & Protection',
    overlayTitle: 'Polybagging & Protection',
    short:
      'Protect your products with professional polybagging and protective packaging designed to reduce transit damage, enhance presentation and meet Amazon FBA and retailer packaging requirements.',
    image: '/Images/PROTECTOVE-PREP-AND-POLY-BAGING.webp',
    introExtra: (
      <>
        Whether you ship fragile items, high-value products or everyday consumer goods, our team applies the right
        packaging for every order — from polybagging and shrink wrapping to protective void fill and secure sealing.
        This sits alongside our{' '}
        <Link to="/amazon-fba-prep-services">Amazon FBA prep services</Link>,{' '}
        <Link to="/contract-packing">contract packing</Link> and{' '}
        <Link to="/multi-channel-fulfillment-services">multi-channel fulfilment</Link> so stock stays protected from
        goods-in through carrier handover.
      </>
    ),
    bullets: [
      <>
        Protective polybagging, shrink wrapping and secure packaging — aligned with{' '}
        <Link to="/blog/amazon-poly-bag-requirements-uk">Amazon poly bag requirements UK</Link>
      </>,
      'Damage-reduction packaging for safe storage and transit',
      <>
        FBA-compliant packaging and labelling options, including{' '}
        <Link to="/blog/amazon-fba-polybagging-suffocation-warning-rules-2026-guide">
          polybagging &amp; suffocation warning rules
        </Link>
      </>,
      'Carrier-ready dispatch for DTC, retail and marketplace orders',
      <>
        Custom packaging for fragile, oversized and high-value products — see also our{' '}
        <Link to="/blog/amazon-fba-packaging-requirements-complete-seller-guide">
          Amazon FBA packaging requirements guide
        </Link>
      </>,
    ],
    quotePanel: (
      <>
        <h2>Professional Polybagging &amp; Protective Packaging</h2>
        <p>
          Our packaging solutions safeguard items throughout storage, handling and transportation so they arrive in
          excellent condition. From standard polybagging and shrink wrapping to customised protection for fragile and
          high-value SKUs, every order is prepared with precision in our{' '}
          <Link to="/fulfilment-facility/amazon-fba-prep">Amazon FBA prep area</Link> workflows when marketplace rules
          apply. As part of our UK fulfilment services, products are securely packed, fully compliant and ready for
          efficient carrier collection — helping reduce returns and improve the customer experience. Need a rate card?{' '}
          <Link to="/pricing-fulfilment-services">View fulfilment pricing UK</Link> or{' '}
          <Link to="/contact-fulfillment-services">request a packing quote</Link>.
        </p>
      </>
    ),
    consultBlurb: (
      <>
        <h2>Why Choose Pick Pack Pro?</h2>
        <p>
          Brands choose our polybagging and protection line for reduced transit damage, improved customer experience,
          Amazon and retailer compliance, faster order processing and professional presentation on every shipment —
          whether you need marketplace-ready prep or everyday DTC protection before carrier handover.
        </p>
      </>
    ),
    tourBlurb: (
      <>
        Book a personalised tour of our fulfilment centre in Milton Keynes and see how everything works behind the
        scenes — from receiving stock and storing inventory to picking, packing and dispatching customer orders.
        You&apos;ll also see how our Mintsoft{' '}
        <Link to="/wms-integrations">WMS integrations</Link> keep stock organised with real-time updates and accurate
        order tracking. It&apos;s a great opportunity to meet our team, ask questions and see how we support growing
        ecommerce brands with reliable, scalable fulfilment.
      </>
    ),
  },
];

function toFacilitySlug(slug?: string): FacilitySlug | null {
  if (!slug) return null;
  const found = facilityCards.find((c) => c.slug === slug);
  return found ? found.slug : null;
}

export default function FulfilmentFacilityDetail() {
  const { slug } = useParams();
  const facilitySlug = toFacilitySlug(slug);
  const facility = facilitySlug ? facilityCards.find((c) => c.slug === facilitySlug) : null;

  if (!facility) {
    return <NotFound />;
  }

  const isFbaPrep = facility.slug === 'amazon-fba-prep';

  return (
    <div className="logistics-page ff-detail-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />
      <section className="fc-section ff-detail-hero">
        <div className="container">
          <div className="ff-detail-back">
            <Link to="/our-fulfilment-center">← Back to our fulfilment centre</Link>
          </div>

          <h1>{facility.title}</h1>
          <p className="ff-detail-short">{facility.short}</p>
          {facility.introExtra ? <p className="ff-detail-intro-extra">{facility.introExtra}</p> : null}

          <div className="ff-detail-grid">
            <div className="ff-detail-media">
              <PictureImage
                src={facility.image}
                alt={facility.overlayTitle}
                className="ff-detail-image"
              />
            </div>

            <div className="ff-detail-panel">
              <h3>What You Can Expect</h3>
              <ul>
                {facility.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>

              {facility.quotePanel ? <div className="ff-detail-quote">{facility.quotePanel}</div> : null}

              <div className="ff-detail-ctas">
                <Link to="/contact-fulfillment-services" className="ff-detail-btn ff-detail-btn-primary">
                  Request a quote →
                </Link>
                <Link to="/book-a-free-call" className="ff-detail-btn ff-detail-btn-secondary">
                  {isFbaPrep ? 'Book a free consultation →' : 'Book a free tour →'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {facility.consultBlurb ? (
        <section className="fc-section ff-detail-consult" aria-labelledby="ff-consult-heading">
          <div className="container ff-detail-narrow">{facility.consultBlurb}</div>
        </section>
      ) : null}

      <section className="fc-section fc-section-alt ff-detail-tour" aria-labelledby="ff-tour-heading">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 id="ff-tour-heading">Want to See It in Person?</h2>
          <p className="ff-detail-tour-copy">
            {facility.tourBlurb ??
              'Book your warehouse tour and we’ll show you how our WMS-driven fulfilment operations work end-to-end.'}
          </p>
          <div className="ff-detail-tour-links">
            <Link to="/book-a-free-call">Book a free tour →</Link>
            <Link to="/pricing-fulfilment-services">View pricing →</Link>
            {isFbaPrep ? <Link to="/amazon-fba-prep-services">FBA prep services →</Link> : null}
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
}
